import React, { useCallback, useRef, useState } from 'react';

import videoCover from '@/pages/Home/images/portal_functionintro_videocover.png';
import { isIOS, isMobileByUA } from '@/utils/ua';

import styles from './index.module.css';

const AgentSection: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isMobile = isMobileByUA();

  const isIOSDevice = isIOS();

  // 简化的播放控制处理，针对移动端优化
  const handlePlayClick = useCallback(
    (e?: React.MouseEvent) => {
      // 移动端简化处理：阻止事件传播但不阻止默认行为
      if (isMobile && e) {
        e.stopPropagation();
        // 移动端不阻止默认行为，让系统处理视频播放
      } else if (e) {
        // 桌面端保持原有逻辑
        e.preventDefault();
        e.stopPropagation();
      }

      if (videoRef.current) {
        // 移动端简化状态管理
        if (isMobile) {
          // 移动端设备直接播放，不手动控制暂停
          if (!isPlaying) {
            videoRef.current.play().catch((error) => {
              console.warn('Video play failed:', error);
            });
          }
        } else {
          // 桌面端保持原有逻辑
          if (isPlaying) {
            videoRef.current.pause();
          } else {
            videoRef.current.play().catch((error) => {
              console.warn('Video play failed:', error);
            });
          }
        }
      }
    },
    [isMobile, isPlaying],
  );

  // 监听视频播放状态变化
  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleVideoEnd = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <section className={styles.agentSection}>
      <div className={styles.agentContainer}>
        {/* Header with Agent label and decorative lines */}
        <div className={styles.headerSection}>
          <div className={styles.agentLabel}>
            <div className={styles.leftLine}></div>
            <span className={styles.agentText}>Coding Agent Inside</span>
            <div className={styles.rightLine}></div>
          </div>
          <div className={styles.textContent}>
            <h2 className={styles.mainTitle}>Understand Codebase and You</h2>
            <p className={styles.description}>
              <span className={styles.grayText}>Think it, say it - </span>
              <span className={styles.gradientText}>CodeFlicker </span>
              <span className={styles.grayText}>
                collaborates with you across your codebase to plan, reason, {'\n'}and deliver
                production-ready code.
              </span>
            </p>
          </div>
        </div>

        {/* Video/Demo Section */}
        <div className={`${styles.videoSection} ${isMobile ? styles.mobileVideoSection : ''}`}>
          <div
            className={`${styles.videoContainer} ${isMobile ? styles.mobileVideoContainer : ''}`}
            // 移动端去除容器点击事件，避免与视频控制冲突
            {...(!isMobile && { onClick: (e) => handlePlayClick(e) })}
          >
            <video
              ref={videoRef}
              className={`${styles.video} ${isMobile ? styles.mobileVideo : ''}`}
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onEnded={handleVideoEnd}
              poster={videoCover}
              preload={isMobile ? 'none' : 'metadata'} // 移动端延迟加载
              controls={true} // 移动端完全禁用控制条，PC端播放时显示
            >
              {/* 优先使用 webm 格式 */}
              {/* 备用 mp4 格式 */}
              <source
                src="https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/Portal_Functionintro_Video0902.mp4"
                type="video/mp4"
              />
              <source
                src="https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/Portal_Functionintro_Video0902.webm"
                type="video/webm"
              />
              您的浏览器不支持视频播放。
            </video>

            {/* 播放控制按钮 - 移动端和桌面端都保留 */}
            {!isPlaying && !isMobile && (
              <div
                className={`${styles.playIconCircle} ${isMobile ? styles.mobilePlayIconCircle : ''}`}
                onClick={(e) => handlePlayClick(e)}
                role="button"
                aria-label="播放视频"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePlayClick();
                  }
                }}
              >
                <svg
                  className={`${styles.playIcon} ${isMobile ? styles.mobilePlayIcon : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 5.14v13.72L19 12L8 5.14z"
                    fill="white"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentSection;
